class ARQuizQuestion extends HTMLElement {
  constructor() {
    super();

    console.log('ARQuizQuestion initialized');

    this.meta = {
      correctAnswer: this.dataset.correctAnswer || '',
    };

    this.$parentQuiz = this.closest('ar-quiz');

    if (!this.$parentQuiz) {
      console.warn('ar-quiz-question must be used within an ar-quiz component');
      return;
    }

    this.$optionContainers = this.querySelectorAll('li');
    this.$options = this.querySelectorAll('input[type="radio"]');

    this.$form = this.querySelector('form');
    this.$nextButton = this.querySelector('button');

    this._handleFormSubmit = this.handleFormSubmit.bind(this);
    this._handleInputChange = this.handleInputChange.bind(this);

    this.setup();
  }

  connectedCallback() {
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  addEventListeners() {
    if (this.$form) {
      this.$form.addEventListener('submit', this._handleFormSubmit);
    }
    this.$options.forEach($option => {
      $option.addEventListener('change', this._handleInputChange);
    });
  }
  removeEventListeners() {
    if (this.$form) {
      this.$form.removeEventListener('submit', this._handleFormSubmit);
    }
    this.$options.forEach($option => {
      $option.removeEventListener('change', this._handleInputChange);
    });
  }

  setup() {
    // Randomise order of labels
    this.$optionContainers.forEach($container => {
      const options = Array.from(this.$optionContainers);
      const randomIndex = Math.floor(Math.random() * options.length);
      const randomOption = options[randomIndex];
      $container.parentNode.appendChild(randomOption);
    });

    this.$nextButton.disabled = true;
  }

  get isAnswered() {
    return Array.from(this.$options).some($option => $option.checked);
  }

  get isCorrect() {
    const selectedOption = Array.from(this.$options).find($option => $option.checked);
    return selectedOption ? selectedOption.value === this.meta.correctAnswer : false;
  }

  handleFormSubmit(event) {
    event.preventDefault();
    this.$parentQuiz.handleProgress();
  }
  handleInputChange() {
    if (this.isAnswered) {
      this.$nextButton.disabled = false;
    }
  }
}

customElements.define('ar-quiz-question', ARQuizQuestion);

class ARQuiz extends HTMLElement {
  quizID = '';
  constructor() {
    super();

    this.quizID = this.dataset.quizId || `unknown`;

    this.meta = {
      points: this.dataset.points || 0,
      passPercentage: this.dataset.passPercentage || 0,
      messagePass: this.dataset.messagePass || 'Congratulations! You passed the quiz.',
      messageFail:
        this.dataset.messageFail || 'Sorry, you did not pass the quiz. Please try again.',
    };

    this.$questions = this.querySelectorAll('ar-quiz-question');

    this.currentQuestionIndex = 0;

    this.results = {
      questionsAnswered: 0,
      questionsTotal: this.$questions.length,
      questionsCorrect: 0,
      percentage: 0,
      points: 0,
      passed: false,
    };

    this.$variables = {
      score: this.querySelector('[data-variable="score"]'),
      percentage: this.querySelector('[data-variable="percentage"]'),
      points: this.querySelector('[data-variable="points"]'),
      message: this.querySelector('[data-variable="message"]'),
    };

    this.$progressAnimationToggle = this.querySelector('.ar-quiz__progress__toggle');
    this.progressAnimation = true;

    this._handleProgressAnimationToggle = this.handleProgressAnimationToggle.bind(this);

    this.setup();
  }

  connectedCallback() {
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  addEventListeners() {
    if (this.$progressAnimationToggle) {
      this.$progressAnimationToggle.addEventListener('click', this._handleProgressAnimationToggle);
    }
  }
  removeEventListeners() {
    if (this.$progressAnimationToggle) {
      this.$progressAnimationToggle.removeEventListener(
        'click',
        this._handleProgressAnimationToggle,
      );
    }
  }

  setup() {
    this.handleProgress();
  }

  get status() {
    return this.dataset.status || 'questions';
  }
  set status(value) {
    this.dataset.status = value;
  }

  calculateScore() {
    this.results.questionsAnswered = 0;
    this.results.questionsCorrect = 0;

    this.$questions.forEach($question => {
      if ($question.isAnswered) {
        this.results.questionsAnswered++;
        if ($question.isCorrect) {
          this.results.questionsCorrect++;
        }
      }
    });

    this.results.percentage = Math.round(
      (this.results.questionsCorrect / this.results.questionsTotal) * 100,
    );
    this.results.points =
      Math.round(
        (this.results.questionsCorrect / this.results.questionsTotal) * this.meta.points * 10,
      ) / 10;
    this.results.passed = this.results.percentage >= this.meta.passPercentage;
  }

  showQuestion(targetIndex = false) {
    this.$questions.forEach(($question, index) => {
      $question.hidden = index === targetIndex ? false : true;
    });
  }

  handleProgress() {
    this.calculateScore();

    this.currentQuestionIndex = this.results.questionsAnswered;
    this.showQuestion(this.currentQuestionIndex);

    this.$variables.score.textContent = this.results.questionsCorrect;
    this.$variables.percentage.textContent = this.results.percentage;
    this.$variables.points.textContent = this.results.points;
    this.$variables.message.textContent = this.results.passed
      ? this.meta.messagePass
      : this.meta.messageFail;

    if (this.currentQuestionIndex >= this.results.questionsTotal) {
      this.status = 'results';

      if (typeof dataLayer != 'undefined') {
        dataLayer.push({
          event: 'eventTracking',
          eventTrackingCategory: 'quizForm',
          eventTrackingAction: 'completion',
          eventTrackingLabel: this.quizID,
        });
        console.log('tracking quiz completion');
      }
    } else {
      this.status = 'questions';
    }

    this.setAttribute('data-result', this.results.passed ? 'pass' : 'fail');

    this.style.setProperty(
      '--ar-progress-percent',
      `${(this.results.questionsAnswered / this.results.questionsTotal) * 100}`,
    );
  }

  handleProgressAnimationToggle() {
    this.progressAnimation = !this.progressAnimation;
    if (this.progressAnimation) {
      this.$progressAnimationToggle.textContent = 'Stop animation';
      this.setAttribute('data-progress-animation', 'on');
    } else {
      this.$progressAnimationToggle.textContent = 'Start animation';
      this.setAttribute('data-progress-animation', 'off');
    }
  }
}

customElements.define('ar-quiz', ARQuiz);
